// Точка входа клиента.
//
// Здесь и только здесь всё, что связано с настоящим браузером:
// хранилище, документ, адресная строка, сеть. Компоненты получают это
// свойствами — потому и проверяются тестами без подмены модулей.

import { createRoot } from 'react-dom/client'
import { createApi } from './api.js'
import { createSession } from './auth/session.js'
import { deviceToken } from './device.js'
import { changeLanguage, initI18n, type Language } from './i18n.js'
import { initialLanguage } from './language.js'
import { initErrorMonitoring, reportError } from './monitoring.js'
import { registerServiceWorker } from './service-worker.js'
import { readSession } from './session.js'
import { applyTheme, initialTheme } from './theme.js'
import { App } from './ui/app.js'
import { I18nProvider } from './ui/i18n-context.js'
import { renderUpdateBanner, updateBannerLabels } from './update-banner.js'
import './app.css'
import './print-sheet.css'

initErrorMonitoring()

// Тема применяется до первой отрисовки: иначе тёмная система получает
// белую вспышку на каждом заходе (D10).
applyTheme(document.documentElement, initialTheme(localStorage, window))

async function start(): Promise<void> {
  // Язык: выбор человека, если он был, иначе язык браузера (D11).
  // До этого выбирать было негде — с английского браузера
  // приложение открывалось по-английски и переключиться было нечем.
  let language = initialLanguage(localStorage, navigator.language)
  let t = await initI18n(language)
  const container = document.querySelector('#app')
  if (container === null) throw new Error('корневой узел #app не найден')

  const invite = readSession(window.location.search)
  const device = deviceToken(localStorage, crypto)

  // Два клиента, и это не дублирование. Первый нужен самой сессии,
  // чтобы обновлять и гасить токены, и ходит он без заголовка
  // авторизации: слать протухший токен на обновление — второй слой
  // той самой гонки (auth/session.ts). Второй — обычный клиент
  // приложения, и он уже знает про сессию.
  const bare = createApi({ device })
  const session = createSession(
    {
      refreshTokens: (token) => bare.refreshTokens(token),
      logout: (token, allDevices) => bare.logout(token, allDevices),
    },
    localStorage,
  )
  const api = createApi({ device, session })

  // Восстановление сессии на старте: access-токен живёт только
  // в памяти вкладки (D35), поэтому после перезагрузки его нет —
  // есть refresh-токен в хранилище, и первым делом он меняется
  // на свежую пару.
  if (session.restorable() && (await session.refresh())) {
    try {
      session.setProfile((await api.me()).profile)
    } catch {
      // Профиль не пришёл — сессии нет. Приложение открывается
      // как для неавторизованного, а не падает на первом экране.
      session.forget()
    }
  }

  const root = createRoot(container)

  /**
   * Отрисовка с текущим словарём.
   *
   * Смена языка перерисовывает всё дерево с новым `t`: контекст
   * перевода отдаёт функцию, а не язык, и подменить её изнутри
   * нельзя — она пришла снаружи. React при этом сверяет дерево
   * с прежним, а не собирает заново: состояние экранов переживает
   * переключение.
   */
  const draw = (): void => {
    root.render(
      <I18nProvider t={t}>
        <App
          api={api}
          storage={localStorage}
          view={window}
          root={document.documentElement}
          campaignId={invite.campaignId}
          seatId={invite.seatId}
          startLevel={invite.startLevel}
          session={session}
          locale={language}
          onLanguage={(next: Language) => {
            language = next
            void changeLanguage(next).then((translate) => {
              t = translate
              document.documentElement.lang = next
              draw()
            })
          }}
          hash={window.location.hash}
        />
      </I18nProvider>,
    )
  }

  document.documentElement.lang = language
  draw()

  // Регистрация после первой отрисовки и после словаря: полосе
  // «доступно обновление» нужен перевод, а приложению — экран,
  // который не ждёт worker'а.
  void registerServiceWorker(() => {
    renderUpdateBanner(document.body, updateBannerLabels(t), document, () => {
      window.location.reload()
    })
  })
}

void start().catch((error: unknown) => {
  reportError(error, { scope: 'bootstrap' })
})
